import React, { useEffect, useState } from "react";
import { Box, Button, Typography, Switch } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  getSingleSitesAction,
  getSiteUnitListAction,
} from "../../../../store/reducers/storageSettingSlice";
import SiteUnitTable from "./SiteUnitTable";
import CustomButton from "../../../../components/common/button/CustomButton";

/* Dot separator - 4px circle */
const Dot = () => (
  <Box
    component="span"
    sx={{
      width: "4px",
      height: "4px",
      borderRadius: "50%",
      backgroundColor: "#666666",
      display: "inline-block",
      flexShrink: 0,
    }}
  />
);

/* Info item: label (gray) + value (dark) */
const InfoItem = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
    <Typography
      component="span"
      sx={{
        fontFamily: "'Inter', sans-serif",
        fontWeight: 400,
        fontSize: "14px",
        lineHeight: "17px",
        color: "#637381",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
    <Typography
      component="span"
      sx={{
        fontFamily: "'Inter', sans-serif",
        fontWeight: 500,
        fontSize: "14px",
        lineHeight: "17px",
        color: "#212B36",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </Typography>
  </Box>
);

/* Stat item: label (gray) + value (dark bold) - 16px */
const StatItem = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
    <Typography
      component="span"
      sx={{
        fontFamily: "'Inter', sans-serif",
        fontWeight: 400,
        fontSize: "16px",
        lineHeight: "19px",
        color: "#637381",
      }}
    >
      {label}
    </Typography>
    <Typography
      component="span"
      sx={{
        fontFamily: "'Inter', sans-serif",
        fontWeight: 600,
        fontSize: "16px",
        lineHeight: "19px",
        color: "#212B36",
      }}
    >
      {value}
    </Typography>
  </Box>
);

interface StorageDetailProps {
  selectedSiteId?: any;
  onAddUnit: () => void;
  onAssignUnit: (unitId: string) => void;
  onEditUnit: (unitId: string) => void;
  onDeleteUnit: (unitId: string) => void;
  onAssignMultiple: () => void;
  onEditSite: () => void;
}

const SiteDetail: React.FC<StorageDetailProps> = ({
  selectedSiteId,
  onAddUnit,
  onAssignUnit,
  onEditUnit,
  onDeleteUnit,
  onAssignMultiple,
  onEditSite,
}) => {
  const dispatch = useDispatch();
  const [filterOption, setFilterOptions] = useState({
    page: 1,
    limit: 10,
    orderBy: "",
    orderIn: "asc",
    siteId: null,
  });
  const { siteDetails, siteUnitList } = useSelector(
    (state: any) => state.storageSetting,
  );
  useEffect(() => {
    if (selectedSiteId) {
      dispatch(getSingleSitesAction(selectedSiteId));
      dispatch(
        getSiteUnitListAction({
          ...filterOption,
          siteId: selectedSiteId,
        }),
      );
    }
  }, [selectedSiteId]);

  const [viewMode, setViewMode] = useState<"list" | "map">("list");
  const [activeFloor, setActiveFloor] = useState(1);
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
  const [selectedUnits, setSelectedUnits] = useState<string[]>([]);

  const handleSelectUnit = (unitId: string) => {
    setSelectedUnits((prev) =>
      prev.includes(unitId)
        ? prev.filter((id) => id !== unitId)
        : [...prev, unitId],
    );
  };

  const floorUnits = siteDetails?.units?.filter(
    (u: any) => u.floorNumber === activeFloor,
  );

  const availableUnitsCount = floorUnits?.filter(
    (u: any) => u.status?.toUpperCase() === "AVAILABLE",
  ).length;

  const rowsInEachFloor =
    siteDetails?.number_of_units && siteDetails?.bays_per_row
      ? Math.ceil(siteDetails.number_of_units / siteDetails.bays_per_row)
      : 0;

  const handleEditSite = () => {
    // Implement the logic to edit the site details
    onEditSite();
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {/* Section 1: Storage Info + List/Map Toggle */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {/* Top Info Bar */}
          <Box
            sx={{
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-start",
              padding: "12px",
              backgroundColor: "#FFFFFF",
              border: "1px solid #C4CDD5",
              borderRadius: "4px 4px 4px 0px",
              minHeight: "54px",
              height: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: "12px",

                "@media (max-width: 768px)": {
                  alignItems: "flex-start",
                },

                "@media (max-width: 600px)": {
                  flexDirection: "column",
                  alignItems: "stretch",
                },
              }}
            >
              {/* Left Content */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                  minWidth: 0,
                  flex: 1,

                  "@media (max-width: 600px)": {
                    width: "100%",
                    rowGap: "6px",
                    columnGap: "8px",
                  },
                }}
              >
                <InfoItem label="Site Name:" value={siteDetails?.site_name} />
                <Dot />
                <InfoItem label="Telephone no.:" value={siteDetails?.phone} />
                <Dot />
                <InfoItem label="Site Type:" value={siteDetails?.site_type} />
                <Dot />
                <InfoItem
                  label="Site Status:"
                  value={
                    siteDetails?.status === "InActive"
                      ? "In Active"
                      : siteDetails?.status
                  }
                />
              </Box>

              {/* Right Button */}
              <Box sx={{ flexShrink: 0 }}>
                <CustomButton title="Edit Site" onClick={handleEditSite} />
              </Box>
            </Box>
          </Box>

          {/* List / Map Toggle */}
          <Box
            sx={{
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-start",
              padding: "0px 12px",
              backgroundColor: "#FFFFFF",
              borderWidth: "0px 1px 1px 1px",
              borderStyle: "solid",
              borderColor: "#C4CDD5",
              borderRadius: "0px 0px 4px 0px",
              height: "64px",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "row", width: "513px" }}>
              <Button
                onClick={() => setViewMode("list")}
                sx={{
                  flex: 1,
                  textTransform: "none",
                  borderRadius: "4px",
                  height: "48px",
                  backgroundColor:
                    viewMode === "list" ? "#02906F" : "transparent",
                  color: viewMode === "list" ? "#FFFFFF" : "#5E5E5E",
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "20px",
                  "&:hover": {
                    backgroundColor:
                      viewMode === "list" ? "#027a5e" : "rgba(0,0,0,0.04)",
                  },
                }}
              >
                List
              </Button>
              <Button
                onClick={() => setViewMode("map")}
                disabled={true}
                sx={{
                  flex: 1,
                  textTransform: "none",
                  borderRadius: "4px",
                  height: "48px",
                  backgroundColor:
                    viewMode === "map" ? "#02906F" : "transparent",
                  color: viewMode === "map" ? "#FFFFFF" : "#5E5E5E",
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "20px",
                  "&:hover": {
                    backgroundColor:
                      viewMode === "map" ? "#027a5e" : "rgba(0,0,0,0.04)",
                  },
                }}
              >
                Map
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Section 2: Post Code Info + Floor Tabs */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {/* Post Code Info Bar */}
          <Box
            sx={{
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-start",
              padding: "12px",
              backgroundColor: "#FFFFFF",
              border: "1px solid #C4CDD5",
              borderRadius: "4px 4px 4px 0px",
              height: "54px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <InfoItem label="Post Code:" value={siteDetails?.postcode} />
              {/* <Dot />
            <InfoItem label="Location Id:" value={siteDetails?.locationId} /> */}
              <Dot />
              <InfoItem
                label="Floors"
                value={siteDetails?.number_of_floors ?? 0}
              />
              <Dot />
              <InfoItem
                label="No. of Units:"
                value={siteDetails?.number_of_units ?? 0}
              />
            </Box>
          </Box>

          {/* Floor Tabs */}
          {/* <Box
          sx={{
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-end",
            padding: "0px 12px",
            backgroundColor: "#FFFFFF",
            borderWidth: "0px 1px 1px 1px",
            borderStyle: "solid",
            borderColor: "#C4CDD5",
            borderRadius: "0px 0px 4px 4px",
            height: "48px",
          }}
        >
          {Array.from({ length: siteDetails?.floors }, (_, i) => {
            const floorNum = i + 1;
            const isActive = activeFloor === floorNum;
            return (
              <Box
                key={i}
                onClick={() => setActiveFloor(floorNum)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "10px",
                  width: "142px",
                  // height: "46px",
                  borderBottom: isActive
                    ? "2px solid #02906F"
                    : "2px solid transparent",
                  cursor: "pointer",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: isActive ? 500 : 400,
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: isActive ? "#02906F" : "#5E5E5E",
                  }}
                >
                  Floor {floorNum}
                </Typography>
              </Box>
            );
          })}
        </Box> */}
        </Box>

        {/* Section 3: Stats + Controls + Table */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {/* Stats + Controls Row */}
          <Box
            sx={{
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px",
              gap: "20px",
              backgroundColor: "#FFFFFF",
              borderWidth: "1px 1px 0px 1px",
              borderStyle: "solid",
              borderColor: "#C4CDD5",
              boxShadow: "0px 4px 5px -1.5px rgba(0, 0, 0, 0.06)",
              borderRadius: "4px 4px 0px 0px",
            }}
          >
            {/* Left: Stats */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <StatItem label="Total Rows:" value={rowsInEachFloor || 0} />
                <Dot />
                <StatItem
                  label="Bays Per Row:"
                  value={siteDetails?.bays_per_row || 0}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <StatItem
                  label="Total Units:"
                  value={siteDetails?.totalUnits ?? 0}
                />
                <Dot />
                <StatItem
                  label="Available Units:"
                  value={siteDetails?.availableUnits ?? 0}
                />
              </Box>
            </Box>

            {/* Right: Controls */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "20px",
              }}
            >
              {/* Toggle */}
              {/* <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Switch
                  checked={showOnlyAvailable}
                  onChange={(e) => {
                    setShowOnlyAvailable(e.target.checked);
                    if (!e.target.checked) setSelectedUnits([]);
                  }}
                  sx={{
                    width: "37px",
                    height: "20px",
                    padding: 0,
                    "& .MuiSwitch-switchBase": {
                      padding: "2px",
                      "&.Mui-checked": {
                        transform: "translateX(17px)",
                        "& + .MuiSwitch-track": {
                          backgroundColor: "#606060",
                          opacity: 1,
                        },
                      },
                    },
                    "& .MuiSwitch-thumb": {
                      width: "16px",
                      height: "16px",
                      backgroundColor: "#FFFFFF",
                    },
                    "& .MuiSwitch-track": {
                      borderRadius: "500px",
                      backgroundColor: "#C4CDD5",
                      opacity: 1,
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: "#212B36",
                  }}
                >
                  Only Available
                </Typography>
              </Box> */}

              {/* Buttons */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {showOnlyAvailable && (
                  <Button
                    onClick={onAssignMultiple}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "8px 20px",
                      height: "40px",
                      backgroundColor: "#606060",
                      borderRadius: "4px",
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "17px",
                      color: "#FFFFFF",
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#505050" },
                    }}
                  >
                    Assign
                  </Button>
                )}
                <CustomButton title={"Add Unit"} onClick={onAddUnit} />
              </Box>
            </Box>
          </Box>

          {/* Table */}
          {viewMode === "list" ? (
            <SiteUnitTable
              selectedSiteId={selectedSiteId}
              floorNumber={activeFloor}
              onAssignUnit={onAssignUnit}
              onEditUnit={onEditUnit}
              onDeleteUnit={onDeleteUnit}
              showOnlyAvailable={showOnlyAvailable}
              selectedUnits={showOnlyAvailable ? selectedUnits : undefined}
              onSelectUnit={showOnlyAvailable ? handleSelectUnit : undefined}
            />
          ) : (
            <></>
            // <StorageMapView
            //   siteDetails={siteDetails}
            //   floorNumber={activeFloor}
            // />
          )}
        </Box>
      </Box>
    </>
  );
};

export default SiteDetail;
